import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import Tabs from '@/components/Tabs.jsx';
import DataTable from '@/components/DataTable.jsx';
import { useLeague } from './league/useLeague.js';
import { renderBarChartRace } from './history/barChartRace.js';
import {
	ManagersTab,
	CaptainsTab,
	ChipsTab,
	SelectionsTab,
	PastSeasonsTab,
	AllTimeTab,
} from './league/leagueTabs.jsx';
import './LeaguePage.css';

const nf = new Intl.NumberFormat('en-GB');

/* Ten colours, cycled — a league can hold more managers than that. */
const RACE_COLOURS = [
	'#e8590c', '#1098ad', '#d6336c', '#1971c2', '#f08c00',
	'#6741d9', '#2f9e44', '#c92a2a', '#0c8599', '#5c940d',
];

function BarChartRace({ data, title }) {
	const ref = useRef(null);
	const stop = useRef(null);
	const [started, setStarted] = useState(false);

	useEffect(() => {
		if (!started || !ref.current || !data) return undefined;
		const keys = Object.keys(data);
		keys.forEach((key, i) => {
			data[key].color = RACE_COLOURS[i % RACE_COLOURS.length];
		});
		stop.current = renderBarChartRace(ref.current, {
			data,
			timelineStart: 1,
			timelineEnd: (data[keys[0]]?.data?.length ?? 2) - 1,
			topN: Math.min(15, keys.length),
			title,
			timelineLabel: (_s, current) => `GW ${current}`,
		});
		return () => stop.current?.();
	}, [started, data, title]);

	if (!started) {
		return (
			<div className="race-placeholder">
				<button className="btn btn--primary" type="button" onClick={() => setStarted(true)}>
					▶ Play the season race
				</button>
			</div>
		);
	}
	return <div className="bar-race__chart" ref={ref} />;
}

/* A non-FPL league is a simple standings table rather than the dashboard. */
function Standings({ div, rows }) {
	return (
		<>
			<header className="league-page__intro">
				<h1>{div}</h1>
				<p className="league-page__lede">Latest points table.</p>
			</header>
			<DataTable
				columns={[
					{ key: 'position', label: 'Pos', numeric: true, defaultSort: 'asc' },
					{ key: 'last_position', label: 'Last', title: 'Last position', numeric: true },
					{ key: 'team', label: 'Team', strong: true },
					{ key: 'played', label: 'P', title: 'Played', numeric: true },
					{ key: 'won', label: 'W', title: 'Won', numeric: true },
					{ key: 'drawn', label: 'D', title: 'Drawn', numeric: true },
					{ key: 'lost', label: 'L', title: 'Lost', numeric: true },
					{ key: 'gd', label: 'GD', title: 'Goal difference', numeric: true },
					{ key: 'total_score', label: 'Total', numeric: true },
					{ key: 'points', label: 'Points', numeric: true },
				]}
				rows={rows ?? []}
				rowKey={(r) => r.team}
				filterPlaceholder="Filter by team"
			/>
		</>
	);
}

export default function LeaguePage() {
	const { div } = useParams();
	const { config, data, status, error, refreshing, refresh } = useLeague(div);

	if (status === 'loading') {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading {div}…</span>
			</div>
		);
	}

	if (status === 'missing' || status === 'error') {
		return (
			<div className="notice notice--error" role="alert">
				<strong>Could not load {div}.</strong>
				<span>{error}</span>
			</div>
		);
	}

	if (data?.nonFplLeague) {
		return (
			<div className="league-page">
				<Standings div={div} rows={data.standings} />
			</div>
		);
	}

	const tabs = [
		{
			id: 'managers',
			label: 'Managers',
			render: () => <ManagersTab standings={data.standings} showPlayed={!data.gwFinished} />,
		},
		data.captains?.length && {
			id: 'captains',
			label: 'Captains / EO',
			render: () => <CaptainsTab captains={data.captains} />,
		},
		{
			id: 'selections',
			label: 'Team Selections',
			render: () => <SelectionsTab selection={data.selection} />,
		},
		data.chips?.length && {
			id: 'chips',
			label: 'Chips',
			render: () => <ChipsTab chips={data.chips} />,
		},
		{
			id: 'past-seasons',
			label: 'Past Seasons',
			render: () => (
				<PastSeasonsTab
					leagueName={config.league_name}
					rows={data.pastSeasonStandings}
					label={data.pastSeasonStandings?.[0]?.season_name}
				/>
			),
		},
		data.hofRank?.length && {
			id: 'all-time',
			label: 'All Time',
			render: () => <AllTimeTab hofRank={data.hofRank} />,
		},
		data.barChartRace && Object.keys(data.barChartRace).length && {
			id: 'race',
			label: 'Season Race',
			render: () => (
				<BarChartRace
					data={data.barChartRace}
					title={` ${config.league_name} season points race`}
				/>
			),
		},
	].filter(Boolean);

	return (
		<div className="league-page">
			<header className="league-page__intro">
				<div className="league-page__title">
					<h1>{config.league_name}</h1>
					{data.leagueLink && (
						<a href={data.leagueLink} target="_blank" rel="noreferrer">
							View on FPL ↗
						</a>
					)}
				</div>
				<div className="league-page__meta">
					<span className="league-page__avg">
						League average <strong className="numeric">{nf.format(data.avgPoints)}</strong>
					</span>
					<button
						className="btn btn--secondary"
						type="button"
						onClick={refresh}
						disabled={refreshing}
					>
						{refreshing ? 'Refreshing…' : 'Refresh'}
					</button>
				</div>
			</header>

			{data.customLeague && (
				<p className="section-note">
					Standings cover the top 250 managers as of the last deadline.
				</p>
			)}

			{data.standings?.length ? (
				<Tabs tabs={tabs} />
			) : (
				<div className="notice notice--info" role="status">
					<strong>Nothing to show yet.</strong>
					<span>This league has no standings recorded. Check back after a gameweek.</span>
				</div>
			)}
		</div>
	);
}
